app.filter('chatUser', function (ChatService) {
    return function (id, property) {
        return ChatService.getUser(id)[property];
    };
}).service('ChatService', function ($http, $rootScope) {
    const service = {isLoading: true};
    const users = {};
    let messagingClient;
    let currentChannel;
    let knownEvent = false;
    let promise;

    service.getUser = (id) => {
        return users[id];
    };

    service.createRoom = () => {
        return $http.get('/chat/create-room');
    };

    service.deleteRoom = (roomId, coachUserId, userId) => {
        return $http.get(`/chat/delete-room/${roomId}`, {
            params: {
                coachUser: coachUserId,
                user: userId
            }
        });
    };

    service.openChat = (receiver, isConnected) => {
        if (service.sender) {
            if (service.receiver) {
                if (service.receiver._id === receiver._id) {
                    return;
                } else {
                    removeListeners(currentChannel);
                }
            }

            service.receiver = receiver;
            service.isConnected = isConnected;
            users[receiver._id] = receiver;
            promise.then(() => {
                findOrCreateChannel(service.sender._id, service.receiver._id)
                    .then(getMessages)
                    .then(addListeners);
            });
        }
    };

    service.closeChat = () => {
        service.receiver = undefined;
    };

    const findChannel = (channelName) => {
        return service.channels.find(channel => channel.uniqueName === channelName);
    };

    const initChannel = (channel) => {
        return messagingClient.getChannelBySid(channel.sid)
            .then(channel => {
                currentChannel = channel;
            });
    };

    const findOrCreateChannel = (senderId, receiverId) => {
        const channelName = getChannelName(senderId, receiverId);
        return new Promise((resolve, reject) => {
            const channel = findChannel(channelName);

            if (!channel) {
                knownEvent = true;
                return messagingClient
                    .createChannel({
                        uniqueName: channelName,
                        friendlyName: channelName,
                        isPrivate: true
                    })
                    .then((channel) => channel.join())
                    .then((channel) => initChannel(channel))
                    .then(() => currentChannel.invite(receiverId))
                    .then(() => resolve())
                    .catch(reject)
                    .finally(() => knownEvent = false);
            }
            channel
                .getMembersCount()
                .then(count => {
                    if (count === 1) {
                        channel.invite(receiverId);
                    }
                    resolve(initChannel(channel));
                });
        });
    };

    const getMessages = () => {
        return currentChannel.getMessages(30)
            .then(function (messages) {
                service.messages = messages.items.map(m => parseMessage(m));
                evaluateMessageExpiry();
            })
            .then(() => $rootScope.$applyAsync())
            .catch((err) => {
                console.error(err);
            });
    };

    const indexMessages = () => {
        return service.messages.reduce((acc, curr) => {
            acc[curr.id] = curr;
            return acc;
        }, {});
    };

    const evaluateMessageExpiry = () => {
        const lastQuotedIndex = findLastIndex(service.messages, (message) => message.type === 'INTERNAL' && message.body.quotes);
        const lastQuotedMessage = service.messages[lastQuotedIndex];
        if (lastQuotedMessage && (Date.now() - (+lastQuotedMessage.timestamp))  > 60 * 60 * 1000) {
            lastQuotedMessage.isExpired = true;
        }

        service.messages.forEach((message, index) => {
            if (message.type === 'INTERNAL' && index < lastQuotedIndex) {
                message.isExpired = true;
            }
        });
    };

    const parseMessage = (message) => {
        const internal = isInternal(message);
        return {
            id: message.sid,
            author: message.author,
            timestamp: message.timestamp,
            type: internal ? 'INTERNAL' : 'TEXT',
            body: internal ? parseBody(message.body) : message.body
        };
    };

    const parseBody = (body) => {
        const url = new URL(body);
        const entries = url.searchParams.entries();
        return paramsToObject(entries);
    };

    function paramsToObject(entries) {
        let result = {};
        for (let entry of entries) { // each 'entry' is a [key, value] tupple
            const [key, value] = entry;
            result[key] = value;
        }
        return result;
    }

    const isInternal = (message) => {
        return message.body.indexOf('msg://studiogym.com') === 0;
    };

    const addListeners = () => {
        currentChannel.on('messageAdded', onMessageAdded);
        currentChannel.on('typingStarted', onTypingStarted);
        currentChannel.on('typingEnded', onTypingEnded);
    };

    const onMessageAdded = (message) => {
        $rootScope.$applyAsync(() => {
            service.messages = service.messages || [];
            service.messages.push(parseMessage(message));
            evaluateMessageExpiry();
        });
    };

    const onTypingStarted = () => {
        service.isTyping = true;
        $rootScope.$applyAsync();
    };

    const onTypingEnded = () => {
        service.isTyping = false;
        $rootScope.$applyAsync();
    };

    service.notifyTyping = () => {
        currentChannel.typing();
    };

    const removeListeners = (channel) => {
        channel.removeListener('messageAdded', onMessageAdded);
        channel.removeListener('typingStarted', onTypingStarted);
        channel.removeListener('typingEnded', onTypingEnded);
    };

    const refreshToken = () => {
        service
            .getToken('TEXT')
            .then((response) => {
                messagingClient.updateToken(response.data.token);
            })
            .catch(console.error);
    };

    function getChannelName(senderId, receiverId) {
        return [senderId, receiverId].sort().join('-');
    }

    const getChannels = (handler) => {
        if (messagingClient === undefined) {
            console.log('Client is not initialized');
            return;
        }

        return messagingClient
            .getSubscribedChannels()
            .then((channels) => {
                service.channels = channels.items;
            });
    };

    const autoJoin = (channel) => {
        channel.join();
    };

    service.getToken = (type, room) => {
        return $http.get('/chat/token', {params: {type, room}});
    };

    service.sendNotification = (receiverId) => {
        return $http({
            method: 'POST',
            url: '/_endpoint/notification/send/' + receiverId,
            data: {},
            responseType: 'json',
        });
    };

    service.initMessagingClient = (token) => {
        promise = Twilio
            .Chat
            .Client
            .create(token)
            .then((client) => {
                messagingClient = client;
                messagingClient.on('tokenExpired', refreshToken);
                messagingClient.on('channelInvited', autoJoin);

                return getChannels().then(() => {
                    service.isLoading = false;
                    $rootScope.$applyAsync();
                });
            });
    };

    service.initText = () => {
        service.getToken('TEXT')
            .then((response) => {
                const token = response.data.token;

                if (!token) {
                    return;
                }
                service.sender = response.data.sender;
                users[service.sender._id] = service.sender;
                service.initMessagingClient(token);
            });
    };

    service.initVideo = (roomName) => {
        return service.getToken('VIDEO', roomName)
            .then((response) => {
                const token = response.data.token;

                if (!token) {
                    return;
                }

                return service.initRoom(token, roomName)
                    .then((room) => {
                        return {room, roomObject: response.data.room};
                    });
            });
    };


    service.initRoom = (token, roomName) => {
        return Twilio.Video.connect(token, {
            name: roomName,
            video: true,
            audio: true,
        });
    };

    service.sendMessage = (message) => {
        currentChannel.sendMessage(message);
    };

    service.initText();

    return service;
});


function findLastIndex(arr, fn) {
    for (let i = arr.length - 1; i >= 0; i--) {
        const item = arr[i];

        if (fn(item, i)) {
            return i;
        }
    }
    return -1;
}
